import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Package } from "lucide-react";
import { http, eur } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { Logo } from "@/components/site/Logo";
import { AuthForm } from "@/components/site/AuthForm";

const PAY = { paid: "Pagato", pending: "In attesa di pagamento", expired: "Scaduto", failed: "Fallito", refunded: "Rimborsato" };

const MyOrders = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    http.get("/orders/mine").then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => setOrders([]));
  }, []);
  if (orders === null) return null;
  const list = orders.filter((o) => o.payment_status === "paid" || o.payment_status === "refunded");
  if (!list.length) return <p className="py-12 text-mist" data-testid="my-orders-empty">Non hai ancora ordini completati.</p>;
  return (
    <div className="mt-6 grid gap-3" data-testid="my-orders-list">
      {list.map((o) => (
        <article key={o.id} data-testid={`my-order-${o.id}`} className="flex flex-col gap-2 rounded-3xl border border-fg/10 bg-[var(--ink-2)] p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-fg2">{(o.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
            <p className="text-xs text-mist">{new Date(o.created_at).toLocaleDateString("it-IT")} · {PAY[o.payment_status] || o.payment_status}</p>
          </div>
          <p className="font-display text-2xl">{eur(o.amount)}</p>
        </article>
      ))}
    </div>
  );
};

export default function Account() {
  const { user, logout } = useAuth();
  const { setOpen } = useCart();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const onDone = () => {
    if (params.get("next") === "checkout") { navigate("/"); setTimeout(() => setOpen(true), 400); }
  };

  return (
    <main className="mx-auto min-h-screen max-w-4xl px-5 py-10 sm:px-8" data-testid="account-page">
      <header className="flex items-center justify-between">
        <Link to="/" data-testid="account-home-link"><Logo /></Link>
        {user && <button onClick={logout} data-testid="account-logout-button" className="flex items-center gap-2 rounded-full border border-fg/20 px-4 py-2 text-sm hover:bg-fg/5"><LogOut className="h-4 w-4" /> Esci</button>}
      </header>
      {user === null ? null : user ? (
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-14">
          <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[var(--volt)]">Il tuo account</p>
          <h1 className="mt-3 font-display text-5xl font-semibold" data-testid="account-greeting">Ciao, {user.name}.</h1>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/" data-testid="account-shop-link" className="rounded-full bg-[var(--volt)] px-6 py-3 font-semibold text-[var(--ink)]">Continua lo shopping</Link>
            {user.role === "admin" && <Link to="/admin" data-testid="account-admin-link" className="rounded-full border border-fg/20 px-6 py-3">Pannello admin</Link>}
          </div>
          <h2 className="mt-14 flex items-center gap-2 font-display text-2xl"><Package className="h-5 w-5 text-[var(--volt)]" /> I miei ordini</h2>
          <MyOrders />
        </motion.section>
      ) : (
        <section className="mt-14 grid items-start gap-10 md:grid-cols-2">
          <div>
            <h1 className="font-display text-5xl font-semibold leading-[0.95]">Il tuo<br /><span className="text-outline-volt">account</span> PhoneSmartX.</h1>
            <p className="mt-5 text-fg2">Registrati per ordinare e pagare online e per ritrovare tutti i tuoi ordini in un unico posto.</p>
          </div>
          <AuthForm onDone={onDone} />
        </section>
      )}
    </main>
  );
}
